import { useAuthStore } from '@/stores/auth'
import type { ApiResponse } from '@ats/shared'

// Use environment variable in production, proxy in development
const API_BASE = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api/v1`
  : '/api/v1'

class ApiClient {
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    }

    const token = useAuthStore.getState().accessToken
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    return headers
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    const data = await response.json() as ApiResponse<T>

    if (!response.ok) {
      // Handle token expiration
      if (response.status === 401) {
        const refreshed = await this.refreshToken()
        if (!refreshed) {
          useAuthStore.getState().logout()
          throw new Error('Session expired')
        }
      }

      throw new Error(data.error?.message || 'An error occurred')
    }

    return data.data as T
  }

  private async refreshToken(): Promise<boolean> {
    const refreshToken = useAuthStore.getState().refreshToken
    if (!refreshToken) return false

    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      })

      if (!response.ok) return false

      const data = await response.json()
      useAuthStore.getState().setTokens(data.data.accessToken, data.data.refreshToken)
      return true
    } catch {
      return false
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'GET',
      headers: this.getHeaders(),
    })
    return this.handleResponse<T>(response)
  }

  async post<T>(endpoint: string, body?: unknown): Promise<T> {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      headers: this.getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    })
    return this.handleResponse<T>(response)
  }

  async patch<T>(endpoint: string, body: unknown): Promise<T> {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(body),
    })
    return this.handleResponse<T>(response)
  }

  async put<T>(endpoint: string, body: unknown): Promise<T> {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body: JSON.stringify(body),
    })
    return this.handleResponse<T>(response)
  }

  async delete<T>(endpoint: string): Promise<T> {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'DELETE',
      headers: this.getHeaders(),
    })
    return this.handleResponse<T>(response)
  }
}

export const api = new ApiClient()

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api.post<{ accessToken: string; refreshToken: string; user: unknown }>('/auth/login', {
      email,
      password,
    }),

  register: (data: {
    tenantName: string
    tenantSlug: string
    name: string
    email: string
    password: string
  }) =>
    api.post<{
      tenant: { id: string; name: string; slug: string }
      user: unknown
      accessToken: string
      refreshToken: string
    }>('/auth/register', data),

  logout: () => api.post('/auth/logout'),
}

// Jobs API
export const jobsApi = {
  list: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return api.get<{ items: unknown[]; meta: unknown }>(`/jobs${query}`)
  },
  get: (id: string) => api.get<unknown>(`/jobs/${id}`),
  create: (data: unknown) => api.post<unknown>('/jobs', data),
  update: (id: string, data: unknown) => api.patch<unknown>(`/jobs/${id}`, data),
  publish: (id: string) => api.post<unknown>(`/jobs/${id}/publish`),
  pause: (id: string) => api.post<unknown>(`/jobs/${id}/pause`),
  close: (id: string) => api.post<unknown>(`/jobs/${id}/close`),
  delete: (id: string) => api.delete<unknown>(`/jobs/${id}`),
  getApplications: (id: string) => api.get<unknown[]>(`/jobs/${id}/applications`),
  getPipeline: (id: string) => api.get<unknown[]>(`/jobs/${id}/pipeline`),
  getQuestions: (id: string) => api.get<unknown[]>(`/jobs/${id}/questions`),
}

// Candidates API
export const candidatesApi = {
  list: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return api.get<{ items: unknown[]; meta: unknown }>(`/candidates${query}`)
  },
  get: (id: string) => api.get<unknown>(`/candidates/${id}`),
  create: (data: unknown) => api.post<unknown>('/candidates', data),
  update: (id: string, data: unknown) => api.patch<unknown>(`/candidates/${id}`, data),
  delete: (id: string) => api.delete<unknown>(`/candidates/${id}`),
}

// Applications API
export const applicationsApi = {
  list: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return api.get<unknown[]>(`/applications${query}`)
  },
  get: (id: string) => api.get<unknown>(`/applications/${id}`),
  move: (id: string, stageId: string, notes?: string) =>
    api.post<unknown>(`/applications/${id}/move`, { stageId, notes }),
  reject: (id: string, reason?: string) =>
    api.post<unknown>(`/applications/${id}/reject`, { reason }),
  hire: (id: string) => api.post<unknown>(`/applications/${id}/hire`),
  addNote: (applicationId: string, content: string) =>
    api.post<unknown>('/notes', { applicationId, content }),
  create: (data: unknown) => api.post<unknown>('/applications', data),
}

// Interviews API
export const interviewsApi = {
  list: (params?: Record<string, string>) => {
    const query = params ? `?${new URLSearchParams(params)}` : ''
    return api.get<unknown[]>(`/interviews${query}`)
  },
  create: (data: unknown) => api.post<unknown>('/interviews', data),
  update: (id: string, data: unknown) => api.patch<unknown>(`/interviews/${id}`, data),
  cancel: (id: string) => api.delete<unknown>(`/interviews/${id}`),
}

// Dashboard/Stats API
export const dashboardApi = {
  getStats: () => api.get<unknown>('/tenant/stats'),
}
